import { useCallback } from "react";
import type { DisplayMessage } from "./ChatPage";
import type { ChatActionsContext } from "./ChatContextTypes";
import {
  applyConversationStreamEvent,
  isAbortError,
  reconnectConversationStream,
  streamRequest,
} from "./ChatPage.utils";

export function useRegenerateAction({
  activeRequest,
  applyConversationSnapshot,
  conversationRef,
  generationMessageId,
  modelId,
  onAttention,
  refreshConversations,
  setConversation,
  setError,
  setSending,
  setUsageRevision,
  stickToBottom,
  thinkingLevel,
  updateConversation,
}: ChatActionsContext) {
  return useCallback(
    async (message: DisplayMessage) => {
      const current = conversationRef.current;
      if (!current) return;
      setSending(true);
      const controller = new AbortController();
      activeRequest.current = controller;
      setError("");
      stickToBottom.current = true;
      try {
        await streamRequest(
          `/conversations/${current.id}/messages/${message.id}/regenerate/stream`,
          {
            ...(modelId ? { modelId } : {}),
            ...(thinkingLevel ? { thinkingLevel } : {}),
          },
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              conversationId: current.id,
              mergeAssistantMessage: (messages, incoming) => {
                const nextMessages = [...messages];
                const targetIndex = nextMessages.findIndex(
                  (item) =>
                    item.id === message.id ||
                    item.versionGroupId === message.versionGroupId,
                );
                if (targetIndex < 0) return [...nextMessages, incoming];
                const keepThrough =
                  message.role === "assistant" ? targetIndex : targetIndex + 1;
                return [...nextMessages.slice(0, keepThrough), incoming];
              },
            });
            if (event.type === "done") {
              setUsageRevision((revision) => revision + 1);
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== current.id,
            onAttention,
            notifyOnDone: true,
            reconnect: (assistantId) =>
              reconnectConversationStream(current.id, assistantId),
          },
        );
        await refreshConversations();
      } catch (reason) {
        if (isAbortError(reason)) return;
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not regenerate response",
        );
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        if (conversationRef.current?.id === current.id) setSending(false);
      }
    },
    [
      activeRequest,
      applyConversationSnapshot,
      conversationRef,
      generationMessageId,
      modelId,
      onAttention,
      refreshConversations,
      setConversation,
      setError,
      setSending,
      setUsageRevision,
      stickToBottom,
      thinkingLevel,
      updateConversation,
    ],
  );
}
